import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { ItemStore, TemplateDataStore } from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { UserApproval, VersionedEntityId } from '@qtek/shared/models';
import { QuillModule } from 'ngx-quill';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

export interface NdaDialogData {
  participantId: string;
  title?: string;
  dealId: string;
  templateId?: string;
  templateDataId: VersionedEntityId;
}

export interface NdaActionResult {
  action: UserApproval | 'back';
  dealId: string | null;
  participantId: string | null;
}

@Component({
  selector: 'qt-nda-dialog',
  imports: [
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    QuillModule,
    TranslationCoreModule,
    MatIconModule,
  ],
  templateUrl: './nda-dialog.component.html',
  styleUrls: ['./nda-dialog.component.scss'],
})
export class NdaDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('contentArea', { static: false })
  contentArea!: ElementRef<HTMLDivElement>;

  readonly title: string;
  readonly dealId: string | null;
  readonly participantId: string | null;
  readonly templateId: string | null;

  isLoading = true;
  isScrolledToBottom = false;
  isProcessingAction = false;
  private pendingApproval: UserApproval | undefined;

  private readonly destroy$ = new Subject<void>();
  private readonly dialogRef = inject(MatDialogRef<NdaDialogComponent>);
  private readonly data = inject<NdaDialogData>(MAT_DIALOG_DATA);
  private readonly route = inject(ActivatedRoute);
  private readonly templateDataStore = inject(TemplateDataStore);
  private readonly itemStore = inject(ItemStore);
  private readonly injector = inject(Injector);

  private readonly templateDataKey = `${this.data.templateDataId.id}.${this.data.templateDataId.version}`;

  readonly ndaTemplate = computed(
    () =>
      this.templateDataStore.singleTemplateData()(this.templateDataKey)?.body
        ?.body ?? null
  );

  constructor() {
    const queryParams = this.route.snapshot.queryParams;

    this.participantId =
      this.data?.participantId || queryParams['participantId'] || null;
    this.dealId = this.data?.dealId;
    this.templateId = this.data?.templateId;
    this.title = this.data?.title || 'GNR_CONF_AGREEMENT';

    effect(() => {
      if (this.ndaTemplate()) {
        this.isLoading = false;
      }
    });
  }

  ngOnInit(): void {
    this.initializeTemplate();
    this.setupActionListeners();

    this.dialogRef.disableClose = true;
  }

  ngAfterViewInit(): void {
    effect(
      () => {
        if (this.ndaTemplate() && this.contentArea) {
          setTimeout(() => {
            this.setupScrollDetection();
          }, 100);
        }
      },
      { injector: this.injector }
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeTemplate(): void {
    this.templateDataStore.getTemplateData(
      this.data.templateDataId.id,
      this.data.templateDataId.version
    );
  }

  private setupScrollDetection(): void {
    if (!this.contentArea) {
      return;
    }

    const scrollElement = this.contentArea.nativeElement;

    setTimeout(() => {
      const isScrollable =
        scrollElement.scrollHeight > scrollElement.clientHeight;

      if (!isScrollable) {
        this.isScrolledToBottom = true;
        return;
      }

      fromEvent(scrollElement, 'scroll')
        .pipe(debounceTime(300), takeUntil(this.destroy$))
        .subscribe(() => {
          this.checkScrollPosition();
        });

      this.checkScrollPosition();
    }, 500);
  }

  private checkScrollPosition(): void {
    if (!this.contentArea) {
      return;
    }

    if (this.isScrolledToBottom) {
      return;
    }

    const element = this.contentArea.nativeElement;
    const threshold = 10;
    const isAtBottom =
      element.scrollTop + element.clientHeight >=
      element.scrollHeight - threshold;

    if (isAtBottom) {
      this.isScrolledToBottom = true;
    }
  }

  public action(userApproval: UserApproval): void {
    if (this.isProcessingAction) return;

    this.isProcessingAction = true;
    this.pendingApproval = userApproval;
    this.itemStore.sendNdaEvent({
      parId: this.participantId,
      userApproval,
    });
  }

  public back(): void {
    this.closeDialog('back');
  }

  private setupActionListeners(): void {
    this.itemStore.sendNdaEvent$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.isProcessingAction = false;
        const result: NdaActionResult = {
          action: this.pendingApproval,
          dealId: this.dealId,
          participantId: this.participantId,
        };
        this.dialogRef.close(result);
      });

    this.itemStore.sendNdaEventFailure$
      .pipe(takeUntil(this.destroy$))
      .subscribe(sts => {
        console.error('NDA action failed:', sts);
        this.isProcessingAction = false;
      });
  }

  private closeDialog(action: NdaActionResult['action']): void {
    const result: NdaActionResult = {
      action,
      dealId: this.dealId,
      participantId: this.participantId,
    };

    this.dialogRef.close(result);
  }
}

export const ndaDialogConfig = {
  width: 'min(1200px, 90vw)',
  height: 'auto',
  maxHeight: '90vh',
  disableClose: false,
  autoFocus: false,
  panelClass: 'nda-dialog-panel',
};
